import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class KeycloakService {
    private keycloak: Keycloak;
    private isInitialized = false;

    constructor() {
        this.keycloak = new Keycloak(environment.keycloak);
    }

    async init(): Promise<boolean> {
        if (this.isInitialized) {
            return this.keycloak.authenticated || false;
        }

        try {
            const authenticated = await this.keycloak.init({
                onLoad: 'check-sso',
                checkLoginIframe: false,
                pkceMethod: 'S256'
            });

            this.isInitialized = true;
            return authenticated;
        } catch (error) {
            console.error('Keycloak initialization failed:', error);
            return false;
        }
    }

    login(): Promise<void> {
        return this.keycloak.login();
    }

    logout(): Promise<void> {
        return this.keycloak.logout({
            redirectUri: window.location.origin
        });
    }

    isLoggedIn(): boolean {
        return this.keycloak.authenticated || false;
    }

    getToken(): string | undefined {
        return this.keycloak.token;
    }

    private getClaim<T>(claim: string): T | undefined {
        return this.keycloak.tokenParsed?.[claim] as T | undefined;
    }

    getUserRoles(): string[] {
        return this.keycloak.realmAccess?.roles || [];
    }

    hasRole(role: string): boolean {
        return this.getUserRoles().includes(role);
    }

    getUsername(): string {
        return this.keycloak.tokenParsed?.['preferred_username'] ||
            this.keycloak.tokenParsed?.['name'] ||
            this.keycloak.tokenParsed?.['sub'] || '';
    }

    getFullName(): string {
        const token = this.keycloak.tokenParsed;

        if (!token) return '';

        const firstName = token['given_name'] || '';
        const lastName = token['family_name'] || '';

        return `${firstName} ${lastName}`.trim() || this.getUsername();
    }

    getUserInfo(): any {
        return {
            username: this.keycloak.tokenParsed?.['preferred_username'] || '',
            email: this.keycloak.tokenParsed?.['email'] || '',
            firstName: this.keycloak.tokenParsed?.['given_name'] || '',
            lastName: this.keycloak.tokenParsed?.['family_name'] || '',
            userType: this.getUserType() || '',
            identificador: this.getIdentificador() || '',
            roles: this.getUserRoles()
        };
    }

    getUserType(): string {
        return this.getClaim<string>('userType') || '';
    }

    getIdentificador(): string {
        return this.getClaim<string>('identificador') || '';
    }

    getIdentificadorAsNumber(): number | null {
        const identificador = Number(this.getIdentificador());
        return Number.isFinite(identificador) ? identificador : null;
    }

    isTenantAgenteFinanceiro(): boolean {
        return this.getUserType() === 'AGE';
    }

    getIdUser(): string {
        return this.keycloak.tokenParsed?.['sub'] || '';
    }

    async updateToken(): Promise<boolean> {
        try {
            return await this.keycloak.updateToken(30);
        } catch {
            return false;
        }
    }
}
